import React, { useMemo } from 'react';
import { type invoiceLineList } from '@store/Finance/AR/ArTransactions/ArTransactionsModels';
import { useTranslation } from 'react-i18next';
import Loading from '@components/Shared/Loader/Loading';
import { Card } from '@components/card/card';

 
type Props = {
  lines: invoiceLineList[];
  maxTableHeight?: string;
  title?: string;
  badgeText?: string;

  // اختياري: لو عندك lookup للأسماء لاحقاً
  getItemName?: (l: invoiceLineList) => string | null | undefined;
  getUomName?: (l: invoiceLineList) => string | null | undefined;
};

const toNum = (v: any) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

const round2 = (n: number) => Math.round((n + Number.EPSILON) * 100) / 100;

const calcLine = (line: invoiceLineList) => {
  const qty = toNum(line.qty);
  const price = toNum(line.unitPrice);

  const discountAmount = toNum(line.discountAmount);
  const vatRate = toNum(line.vatRate);

  // إذا عندك lineNet/vatAmount/lineGross من السيرفر استخدمها مباشرة
  const net = line.lineNet != null ? toNum(line.lineNet) : round2(qty * price - discountAmount);
  const vatAmount = line.vatAmount != null ? toNum(line.vatAmount) : round2(net * (vatRate / 100));
  const totalIncVat = line.lineGross != null ? toNum(line.lineGross) : round2(net + vatAmount);

  // gross (في نموذجك subTotal يعتمد gross)
  const gross = net;

  return { qty, price, discountAmount, vatRate, vatAmount, totalIncVat, gross };
};

export default function ReferenceInvoiceLines({
  lines,
  maxTableHeight = '400px',
  title,
  badgeText,
  getItemName,
  getUomName
}: Props) {
  const { i18n, t } = useTranslation();

  const locale =   'en-US';
  const fmt2 = useMemo(
    () => new Intl.NumberFormat(locale, { minimumFractionDigits: 2, maximumFractionDigits: 2 }),
    [locale]
  );

  const summary = useMemo(() => {
    const rows = (lines || []).map(calcLine);
    const subTotal = round2(rows.reduce((s, r) => s + r.gross, 0));
    const discountTotal = round2(rows.reduce((s, r) => s + r.discountAmount, 0));
    const vatTotal = round2(rows.reduce((s, r) => s + r.vatAmount, 0));
    const invoiceTotal = round2(rows.reduce((s, r) => s + r.totalIncVat, 0));
    return { subTotal, discountTotal, vatTotal, invoiceTotal };
  }, [lines]);

  const totals = useMemo(
    () => ({
      subTotal: fmt2.format(summary.subTotal),
      discountTotal: fmt2.format(summary.discountTotal),
      vatTotal: fmt2.format(summary.vatTotal),
      invoiceTotal: fmt2.format(summary.invoiceTotal)
    }),
    [summary, fmt2]
  );

  const safeLines = lines || [];

  return (
    <Loading loading={false} error={null} Type='Dots'>
      <div className='mb-4 border-0'>
        {/* Header */}
        <div className='card-header d-flex justify-content-between align-items-center'>
          <h6 className='mb-0'>
            <i className='fas fa-file-invoice me-2'></i>
            {title ?? t('تفاصيل المرجع')}
           </h6>

          <div>
            <span className='badge bg-theme me-2'>
              {i18n.language === 'ar' ? 'عدد البنود:' : 'Lines:'} {safeLines.length}
            </span>
            <span className='badge bg-success'>
              {i18n.language === 'ar' ? 'الإجمالي:' : 'Total:'} {totals.invoiceTotal}
            </span>
          </div>
        </div>

        {/* Summary Totals */}
        {/* <div className='card-body p-3'>
          <div className='bg-inverse bg-opacity-10 p-3 rounded-0'>
            <div className='d-flex flex-wrap gap-4 justify-content-between'>
              <div className='d-flex gap-2 align-items-center'>
                <span className='fw-bold'>{i18n.language === 'ar' ? 'الإجمالي الفرعي' : 'SubTotal'}:</span>
                <span className='fw-bold'>{totals.subTotal}</span>
              </div>

             
              <div className='d-flex gap-2 align-items-center'>
                <span className='fw-bold'>{i18n.language === 'ar' ? 'الخصم' : 'Discount'}:</span>
                <span className='fw-bold text-danger'>{totals.discountTotal}</span>
              </div>

              <div className='d-flex gap-2 align-items-center'>
                <span className='fw-bold'>{i18n.language === 'ar' ? 'ضريبة القيمة المضافة' : 'VAT'}:</span>
                <span className='fw-bold text-warning'>{totals.vatTotal}</span>
              </div>

              <div className='d-flex gap-2 align-items-center'>
                <span className='fw-bold'>{i18n.language === 'ar' ? 'الإجمالي' : 'Total'}:</span>
                <span className='text-theme fs-5 fw-bold'>{totals.invoiceTotal}</span>
              </div>
            </div>
          </div>
        </div> */}

        {/* Table Section */}
        <div className='card-body p-0'>
          <div
            className='table-responsive position-relative'
            style={{
              maxHeight: maxTableHeight,
              overflow: 'auto'
            }}
          >
            <table className='table table-hover mb-0'>
              <thead
                className='table-actived fw-bold'
                style={{
                  position: 'sticky',
                  top: 0,
                  zIndex: 100,
                  backgroundColor: 'var(--bs-table-bg)',
                  backdropFilter: 'blur(30px)',
                  boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                }}
              >
                <tr>
                  <th
                    className='text-center small'
                    style={{
                      width: '3%',
                      position: 'sticky',
                      left: 0,
                      backgroundColor: 'var(--bs-table-bg)',
                      zIndex: 101,
                      boxShadow: '2px 0 4px rgba(0,0,0,0.1)'
                    }}
                  >
                    #
                  </th>

                  <th className='text-center small' style={{ width: '30%' }}>
                    {i18n.language === 'ar' ? 'اسم الصنف' : 'Item'}
                  </th>

                  <th className='text-center small' style={{ width: '12%' }}>
                    {i18n.language === 'ar' ? 'الوحدة' : 'UOM'}
                  </th>

                  <th className='text-center small' style={{ width: '8%' }}>
                    {i18n.language === 'ar' ? 'الكمية' : 'Qty'}
                  </th>

                  <th className='text-center small' style={{ width: '10%' }}>
                    {i18n.language === 'ar' ? 'السعر' : 'Price'}
                  </th>

                  <th className='text-center small' style={{ width: '10%' }}>
                    VAT
                  </th>

                  <th className='text-center small' style={{ width: '12%' }}>
                    {i18n.language === 'ar' ? 'الإجمالي' : 'Total'}
                  </th>
                </tr>
              </thead>

              <tbody>
                {safeLines.map((line, idx) => {
                  const c = calcLine(line);

                  const itemName =
                    getItemName?.(line) ??
                    (line.description?.trim() ? line.description : line.invItemId ? `#${line.invItemId}` : '-');

                  const uomName = getUomName?.(line) ?? (line.uom ? String(line.uom) : '-');

                  return (
                    <tr key={line.id ?? `${idx}`}>
                      <td
                        className='text-center align-middle'
                        style={{
                          position: 'sticky',
                          left: 0,
                          backgroundColor: 'var(--bs-body-bg)',
                          zIndex: 50,
                          boxShadow: '2px 0 4px rgba(0,0,0,0.1)'
                        }}
                      >
                        <span className='badge bg-theme bg-opacity-30 fw-bold'>{idx + 1}</span>
                      </td>

                      <td className='text-center align-middle'>
                        <div className='d-flex flex-column gap-1'>
                          <strong>{itemName}</strong>

                          {/* تفاصيل تقنية صغيرة (اختيارية) */}
                          {(line.invItemId || line.salLineId) && (
                            <small className='text-muted'>
                              {line.invItemId ? <>ItemId: {line.invItemId}</> : null}
                              {line.invItemId && line.salLineId ? ' • ' : null}
                              {line.salLineId ? <>SalLineId: {line.salLineId}</> : null}
                            </small>
                          )}
                        </div>
                      </td>

                      <td className='text-center align-middle'>
                        <small className='text-muted'>{uomName}</small>
                      </td>

                      <td className='text-center align-middle'>
                        <span className='fw-bold'>{fmt2.format(c.qty)}</span>
                      </td>

                      <td className='text-center align-middle'>
                        <span className='fw-bold'>{fmt2.format(c.price)}</span>
                      </td>

                      <td className='text-center align-middle'>
                        <span className='fw-bold text-warning'>{fmt2.format(c.vatAmount)}</span>
                      </td>

                      <td className='text-center align-middle'>
                        <strong className='text-theme fs-6'>{fmt2.format(c.totalIncVat)}</strong>
                      </td>
                    </tr>
                  );
                })}

                {safeLines.length === 0 && (
                  <tr>
                    <td colSpan={7} className='text-center py-4 text-muted'>
                      <i className='fa fa-inbox fa-2x mb-2'></i>
                      <br />
                      {i18n.language === 'ar' ? 'لا توجد بنود للمرجع' : 'No reference lines'}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {safeLines.length > 10 && (
            <div className='mt-2 text-center'>
              <small className='text-muted'>
                <i className='fas fa-arrows-alt-v me-1'></i>
                {i18n.language === 'ar'
                  ? `عرض ${safeLines.length} سطر - استخدم السكرول للتنقل`
                  : `Showing ${safeLines.length} lines - scroll to navigate`}
              </small>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className='card-footer d-flex justify-content-between align-items-center'>
          <div className='text-muted small'>
            <i className='fas fa-list me-1'></i>
            {i18n.language === 'ar'
              ? `عرض ${safeLines.length} من ${safeLines.length} بند`
              : `Showing ${safeLines.length} of ${safeLines.length}`}
          </div>

          <div className='text-theme fw-bold'>
            <i className='fas fa-calculator me-2'></i>
            {i18n.language === 'ar' ? 'إجمالي المرجع:' : 'Reference Total:'} {totals.invoiceTotal}
          </div>
        </div>
      </div>
    </Loading>
  );
}
